import { authedFetch } from '~/utils/authed-fetch'
import { ref, computed } from 'vue'
import { getApiBase } from '~/utils/api-base'

// Inline-edit token for the editor iframes (SEC-2 V5). The backend renders
// `is_preview=1` as editable ONLY with `&et=<token>` — without it the frame is
// read-only — and the only mint is the JWT-authenticated
// GET /api/web/{id}/edit-token/. One token per selected web, renewed at half
// its TTL. A renewal must NOT reload the frames (that cut a sentence off
// mid-typing, QA F047): frames freeze the `et` they were loaded with
// (BlockPreviewIframe / web-edit take a fresh one only when they reload for
// another reason) and a renewal is pushed into the open frames by postMessage
// ('qcms.edit-token', handled in template_index.html). Tokens are bound to the
// user + token_version (F006) — resetEditTokens() on login / password change /
// logout.
// Plain module refs, not useState: the auth store imports resetEditTokens, and
// that import is evaluated outside a Nuxt context (useState there throws
// „[nuxt] instance unavailable"). The app is a client-only SPA, so a module
// ref is the same single shared state.
const state = ref<{ webId: number | null; token: string; expiresAt: number }>({
  webId: null,
  token: '',
  expiresAt: 0,
})
let renewTimer: ReturnType<typeof setTimeout> | null = null
// Template-edit twin: admin-only GET /api/template/{id}/edit-token/ → `?et=`
// on template-block previews (`/api/block/{uid}/render`). Replaces reading the
// JWT from the iframe's own storage, which failed cross-origin in dev.
const tplState = ref<{ templateId: number | null; token: string; expiresAt: number }>({
  templateId: null,
  token: '',
  expiresAt: 0,
})
let tplRenewTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Forget every cached edit token (F006: tokens are bound to the user + their
 * token_version). Called by the auth store on login, password change and
 * logout — otherwise the next editor open in this tab would reuse a token of
 * another user / a revoked one and the frames would render read-only.
 */
export function resetEditTokens() {
  if (renewTimer) clearTimeout(renewTimer)
  if (tplRenewTimer) clearTimeout(tplRenewTimer)
  renewTimer = null
  tplRenewTimer = null
  state.value = { webId: null, token: '', expiresAt: 0 }
  tplState.value = { templateId: null, token: '', expiresAt: 0 }
}

function pushTokenIntoFrames(token: string) {
  if (typeof document === 'undefined') return
  document.querySelectorAll<HTMLIFrameElement>('iframe').forEach((f) => {
    try {
      f.contentWindow?.postMessage({ type: 'qcms.edit-token', token }, '*')
    } catch {
      // cross-origin / detached frame — it gets a fresh token on its next load
    }
  })
}

export function useEditToken() {
  async function ensure(webId: number | null | undefined) {
    if (!webId) return
    const s = state.value
    if (s.webId === webId && s.token && Date.now() < s.expiresAt) return
    try {
      const res = await authedFetch(`${getApiBase()}/api/web/${webId}/edit-token/`)
      if (!res.ok) return
      const body = await res.json()
      const ttlMs = Math.max(60, Number(body.expires_in) || 0) * 1000
      const renewal = s.webId === webId && !!s.token
      state.value = { webId, token: body.token, expiresAt: Date.now() + ttlMs / 2 }
      if (renewal) pushTokenIntoFrames(body.token)
      if (renewTimer) clearTimeout(renewTimer)
      renewTimer = setTimeout(() => ensure(webId), ttlMs / 2)
    } catch {
      // Network hiccup: frames stay read-only until the next ensure().
    }
  }

  async function ensureTemplate(templateId: number | null | undefined) {
    if (!templateId) return
    const s = tplState.value
    if (s.templateId === templateId && s.token && Date.now() < s.expiresAt) return
    try {
      const res = await authedFetch(`${getApiBase()}/api/template/${templateId}/edit-token/`)
      if (!res.ok) return
      const body = await res.json()
      const ttlMs = Math.max(60, Number(body.expires_in) || 0) * 1000
      const renewal = s.templateId === templateId && !!s.token
      tplState.value = { templateId, token: body.token, expiresAt: Date.now() + ttlMs / 2 }
      if (renewal) pushTokenIntoFrames(body.token)
      if (tplRenewTimer) clearTimeout(tplRenewTimer)
      tplRenewTimer = setTimeout(() => ensureTemplate(templateId), ttlMs / 2)
    } catch {
      // Frames fall back to the same-origin JWT (prod) until the next ensure.
    }
  }

  /** `&et=…` for an editable frame URL, '' while no token is loaded. */
  const etQuery = computed(() => (state.value.token ? `&et=${encodeURIComponent(state.value.token)}` : ''))
  const templateEtQuery = computed(() =>
    tplState.value.token ? `&et=${encodeURIComponent(tplState.value.token)}` : '',
  )

  return { ensure, etQuery, ensureTemplate, templateEtQuery }
}
